/**
 * Seção "Promoção" do produto no painel (issue #205, spec §8.1).
 *
 * Um formulário reutilizado nos dois lugares: dentro do editor do produto
 * (ProductModal) e pelo atalho "%" da lista (Menu.tsx abre o mesmo editor e
 * rola até aqui). Percent fixo ou preço final absoluto, janela de dias da
 * semana + horário no fuso do restaurante e data de fim opcional — com
 * prévia "€11,00 → €8,80" calculada em tempo real.
 *
 * Regras e cobrança vivem no servidor (features/promotions); aqui é só a UI
 * sobre POST/PUT /admin/promotions.
 */
import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import type { useQueryClient } from '@tanstack/react-query'
import { PROMOTION_DAYS } from '@self-restaurant/shared'
import { isPromotionActiveAt } from '@self-restaurant/client-core'
import { promotions, type Product, type Promotion } from '../lib/api.js'
import { Button, Input, Select, Badge, toast } from './ui/index.js'
import { cn, formatCents, parseCents } from '../lib/utils.js'
import { weekFirstDay } from '../lib/daily-specials.js'
import { useI18n } from '../lib/i18n.js'

// ─── Estado da promoção (badge da lista e do formulário) ──────────────────────

export type PromotionState = 'on' | 'scheduled' | 'expired' | 'off'

/**
 * 'on' = vigente AGORA (datas + dias/horário no fuso da casa, mesma função
 * pura do servidor); 'scheduled' = ainda não vale (agendada ou fora da
 * janela); 'expired' = já passou; 'off' = desligada à mão.
 */
export function promotionState(
  promo: Pick<
    Promotion,
    | 'is_active'
    | 'starts_at'
    | 'ends_at'
    | 'days_of_week'
    | 'start_time'
    | 'end_time'
    | 'id'
    | 'discount_type'
    | 'discount_value'
    | 'variant_id'
  >,
  timezone: string | null,
  nowMs: number = Date.now(),
): PromotionState {
  if (!promo.is_active) return 'off'
  const nowSec = Math.floor(nowMs / 1000)
  if (isPromotionActiveAt(promo, nowSec, timezone)) return 'on'
  if (promo.ends_at !== null && nowSec >= promo.ends_at) return 'expired'
  return 'scheduled'
}

/** Badge de estado da promoção no card da lista e no cabeçalho da seção. */
export function PromotionStatusBadge({
  promo,
  timezone,
  currency,
  locale,
  priceLocale = '',
}: {
  promo: Promotion
  timezone: string | null
  currency: string
  locale: string
  /** #428: locale de formatação Intl (língua base + país do restaurante) */
  priceLocale?: string
}) {
  const { t } = useI18n()
  const state = promotionState(promo, timezone)
  const label = (() => {
    if (state === 'expired') return t('admin.promo.state_expired')
    if (state === 'scheduled') return t('admin.promo.state_scheduled')
    if (state === 'off') return t('admin.promo.state_off')
    return promo.discount_type === 'percent'
      ? t('admin.promo.state_percent', { value: String(promo.discount_value) })
      : t('admin.promo.state_fixed', {
          price: formatCents(promo.discount_value, currency, priceLocale || locale),
        })
  })()
  return (
    <Badge
      color={state === 'on' ? 'green' : state === 'off' ? 'gray' : 'yellow'}
      className="text-xs"
    >
      {label}
    </Badge>
  )
}

// ─── Formulário ───────────────────────────────────────────────────────────────

/** bit 0 = segunda … bit 6 = domingo (ordem da UI), rótulo via hours.day_n. */
const DAY_BITS: Array<{ bit: number; dayKey: string }> = [
  { bit: PROMOTION_DAYS.mon, dayKey: 'admin.branding.hours.day_1' },
  { bit: PROMOTION_DAYS.tue, dayKey: 'admin.branding.hours.day_2' },
  { bit: PROMOTION_DAYS.wed, dayKey: 'admin.branding.hours.day_3' },
  { bit: PROMOTION_DAYS.thu, dayKey: 'admin.branding.hours.day_4' },
  { bit: PROMOTION_DAYS.fri, dayKey: 'admin.branding.hours.day_5' },
  { bit: PROMOTION_DAYS.sat, dayKey: 'admin.branding.hours.day_6' },
  { bit: PROMOTION_DAYS.sun, dayKey: 'admin.branding.hours.day_0' },
]

const daysToMask = (days: Set<number>): number | null =>
  days.size === 0 ? null : [...days].reduce((a, b) => a | b, 0)

const secondsToEndDate = (sec: number | null): string =>
  sec === null ? '' : new Date(sec * 1000).toISOString().slice(0, 10)

/** Data escolhida (yyyy-MM-dd) → fim do dia no aparelho, em Unix seconds. */
const endDateToSeconds = (date: string): number | null => {
  if (!date) return null
  const end = new Date(`${date}T23:59:59`)
  return Number.isNaN(end.getTime()) ? null : Math.floor(end.getTime() / 1000)
}

export function PromotionForm({
  product,
  locale,
  currency,
  priceLocale = '',
  timezone,
  countryCode,
  qc,
}: {
  product: Product
  locale: string
  currency: string
  /** #428: locale de formatação Intl (língua base + país do restaurante) */
  priceLocale?: string
  timezone: string | null
  /** #428: país do tenant — início da semana nos dias da promoção */
  countryCode?: string | null
  qc: ReturnType<typeof useQueryClient>
}) {
  const { t, lang } = useI18n()
  // #428: os dias são mostrados a começar pelo 1.º dia da semana do PAÍS do
  // restaurante (pt+PT → segunda, pt+BR/en+US → domingo) — os bits não mudam
  const orderedDayBits = [...DAY_BITS]
  // getDay (0=domingo) → bit da UI (bit 0 = segunda … bit 6 = domingo)
  const firstBit = [64, 1, 2, 4, 8, 16, 32][weekFirstDay(lang, countryCode)]
  const firstIdx = orderedDayBits.findIndex((d) => d.bit === firstBit)
  if (firstIdx > 0) orderedDayBits.push(...orderedDayBits.splice(0, firstIdx))
  const [type, setType] = useState<'percent' | 'fixed_price'>('percent')
  const [valueStr, setValueStr] = useState('20')
  const [days, setDays] = useState<Set<number>>(new Set(DAY_BITS.map((d) => d.bit)))
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [endDate, setEndDate] = useState('')
  const [label, setLabel] = useState('')
  const [variantId, setVariantId] = useState('')

  // promoção ativa do produto (ou a última desligada, como histórico)
  const { data: promos = [], isLoading } = useQuery({
    queryKey: ['promotions', product.id],
    queryFn: () => promotions.list(product.id),
  })
  const active = promos.find((p) => p.is_active)

  // ao carregar a promoção existente, o formulário assume os valores dela
  const [loadedFor, setLoadedFor] = useState<string | null>(null)
  useEffect(() => {
    const source = active ?? promos[0]
    if (!source || loadedFor === source.id) return
    setLoadedFor(source.id)
    setType(source.discount_type)
    setValueStr(
      String(
        source.discount_type === 'percent' ? source.discount_value : source.discount_value / 100,
      ),
    )
    setDays(
      new Set(
        source.days_of_week === null
          ? DAY_BITS.map((d) => d.bit)
          : DAY_BITS.filter((d) => (source.days_of_week! & d.bit) !== 0).map((d) => d.bit),
      ),
    )
    setStartTime(source.start_time ?? '')
    setEndTime(source.end_time ?? '')
    setEndDate(secondsToEndDate(source.ends_at))
    setLabel(source.label ?? '')
    setVariantId(source.variant_id ?? '')
  }, [active, promos, loadedFor])

  const basePrice = useMemo(() => {
    if (type === 'fixed_price' && variantId) {
      return product.variants.find((v) => v.id === variantId)?.price_cents ?? product.price_cents
    }
    return product.price_cents
  }, [type, variantId, product])

  const preview = useMemo(() => {
    const value = type === 'percent' ? parseInt(valueStr || '0', 10) : parseCents(valueStr)
    if (!Number.isFinite(value) || value <= 0) return null
    const final = type === 'percent' ? Math.round((basePrice * (100 - value)) / 100) : value
    if (final >= basePrice) return null
    return { list: basePrice, final }
  }, [type, valueStr, basePrice])

  const invalid =
    preview === null ||
    (type === 'percent' &&
      (parseInt(valueStr || '0', 10) < 1 || parseInt(valueStr || '0', 10) > 90)) ||
    (startTime !== '' && endTime === '') ||
    (startTime === '' && endTime !== '')

  const body = () => ({
    discount_type: type,
    discount_value: type === 'percent' ? parseInt(valueStr, 10) : parseCents(valueStr),
    ...(type === 'fixed_price' && variantId ? { variant_id: variantId } : {}),
    days_of_week: daysToMask(days),
    ...(startTime && endTime
      ? { start_time: startTime, end_time: endTime }
      : { start_time: null, end_time: null }),
    ends_at: endDateToSeconds(endDate),
    label: label.trim() || null,
  })

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['promotions'] })
    void qc.invalidateQueries({ queryKey: ['promotions', product.id] })
  }

  const saveMut = useMutation({
    mutationFn: () =>
      active
        ? promotions.update(active.id, body())
        : promotions.create({ ...body(), product_id: product.id, is_active: true }),
    onSuccess: () => {
      refresh()
      toast.success(active ? t('admin.promo.updated') : t('admin.promo.created'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const toggleMut = useMutation({
    mutationFn: () => promotions.setActive(active!.id, !active!.is_active),
    onSuccess: () => {
      refresh()
      toast.success(t('admin.promo.updated'))
    },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <div
      data-testid={`catalog-promo-${product.id}`}
      className="rounded-xl border border-gray-200 p-4"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-sm font-medium text-gray-700">{t('admin.promo.section')}</p>
        {isLoading ? (
          <span className="text-xs text-gray-400">{t('admin.common.loading')}</span>
        ) : active ? (
          <PromotionStatusBadge
            priceLocale={priceLocale}
            promo={active}
            timezone={timezone}
            currency={currency}
            locale={locale}
          />
        ) : (
          <span className="text-xs text-gray-400">{t('admin.promo.none')}</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select
          label={t('admin.promo.type')}
          data-testid="catalog-promo-type"
          value={type}
          onChange={(e) => setType(e.target.value as 'percent' | 'fixed_price')}
          options={[
            { value: 'percent', label: t('admin.promo.type_percent') },
            { value: 'fixed_price', label: t('admin.promo.type_fixed') },
          ]}
        />
        <Input
          label={type === 'percent' ? t('admin.promo.value_percent') : t('admin.promo.value_fixed')}
          data-testid="catalog-promo-value"
          type="number"
          min="1"
          max={type === 'percent' ? 90 : undefined}
          step={type === 'percent' ? 1 : undefined}
          value={valueStr}
          onChange={(e) => setValueStr(e.target.value)}
        />
      </div>

      {type === 'fixed_price' && product.variants.length > 0 && (
        <div className="mt-3">
          <Select
            label={t('admin.promo.variant')}
            data-testid="catalog-promo-variant"
            value={variantId}
            onChange={(e) => setVariantId(e.target.value)}
            options={[
              { value: '', label: '—' },
              ...product.variants.map((v) => ({
                value: v.id,
                label: `${v.name} · ${formatCents(v.price_cents, currency, priceLocale || locale)}`,
              })),
            ]}
          />
          <p className="text-xs text-gray-500 mt-1">{t('admin.promo.variant_required')}</p>
        </div>
      )}

      {/* dias da semana */}
      <div className="mt-3">
        <p className="text-sm font-medium text-gray-700 mb-1">{t('admin.promo.days')}</p>
        <div className="flex flex-wrap gap-1" data-testid="catalog-promo-days">
          {orderedDayBits.map(({ bit, dayKey }) => {
            const on = days.has(bit)
            return (
              <button
                key={bit}
                type="button"
                data-testid={`catalog-promo-day-${bit}`}
                aria-pressed={on}
                onClick={() =>
                  setDays((prev) => {
                    const next = new Set(prev)
                    if (next.has(bit)) next.delete(bit)
                    else next.add(bit)
                    return next
                  })
                }
                className={cn(
                  'px-2 py-1 rounded-md text-xs border transition-colors',
                  on
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50',
                )}
              >
                {t(dayKey).slice(0, 3)}
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
        <Input
          label={t('admin.promo.start_time')}
          data-testid="catalog-promo-start-time"
          type="time"
          value={startTime}
          onChange={(e) => setStartTime(e.target.value)}
        />
        <Input
          label={t('admin.promo.end_time')}
          data-testid="catalog-promo-end-time"
          type="time"
          value={endTime}
          onChange={(e) => setEndTime(e.target.value)}
        />
        <Input
          label={t('admin.promo.ends_at')}
          data-testid="catalog-promo-end-date"
          type="date"
          value={endDate}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>
      <p className="text-xs text-gray-500 mt-1">
        {t('admin.promo.window_hint', { timezone: timezone ?? '—' })}
      </p>

      <div className="mt-3">
        <Input
          label={t('admin.promo.label')}
          data-testid="catalog-promo-label"
          value={label}
          maxLength={60}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Happy hour"
        />
      </div>

      {/* prévia do preço (spec §8.1: €11,00 → €8,80) */}
      <div
        data-testid={`catalog-promo-preview-${product.id}`}
        className="mt-3 rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-sm text-indigo-800"
      >
        {preview ? (
          <>
            <s className="opacity-60 mr-2" data-testid="catalog-promo-preview-list">
              {formatCents(preview.list, currency, priceLocale || locale)}
            </s>
            →{' '}
            <strong data-testid="catalog-promo-preview-final">
              {formatCents(preview.final, currency, priceLocale || locale)}
            </strong>
          </>
        ) : (
          <span className="text-indigo-500">{t('admin.promo.preview_invalid')}</span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 mt-3">
        <Button
          size="sm"
          data-testid="catalog-promo-save"
          loading={saveMut.isPending}
          disabled={invalid}
          onClick={() => saveMut.mutate()}
        >
          {active ? t('admin.common.save') : t('admin.promo.create')}
        </Button>
        {active && (
          <Button
            size="sm"
            variant={active.is_active ? 'danger' : 'secondary'}
            data-testid="catalog-promo-toggle"
            loading={toggleMut.isPending}
            onClick={() => toggleMut.mutate()}
          >
            {active.is_active ? t('admin.promo.turn_off') : t('admin.promo.turn_on')}
          </Button>
        )}
      </div>
    </div>
  )
}
